import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

let loadPromise: Promise<FFmpeg> | null = null;

async function loadFFmpeg(): Promise<FFmpeg> {
  const instance = new FFmpeg();
  const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/esm';
  console.log('[FFmpeg] Loading WASM core in background...');
  const t0 = performance.now();
  try {
    const [coreURL, wasmURL] = await Promise.all([
      toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
      toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
    ]);
    await instance.load({ coreURL, wasmURL });
    console.log(`[FFmpeg] Ready — loaded in ${((performance.now() - t0) / 1000).toFixed(2)}s`);
    return instance;
  } catch (e) {
    console.error('[FFmpeg] Failed to load:', e);
    loadPromise = null; // allow retry on next use
    throw e;
  }
}

function getFFmpeg(): Promise<FFmpeg> {
  if (!loadPromise) {
    loadPromise = loadFFmpeg();
  }
  return loadPromise;
}

/** Call once on app startup to warm up FFmpeg in the background. */
export function preloadFFmpeg(): void {
  getFFmpeg().catch((e) => {
    console.error('[FFmpeg] Preload failed:', e);
  });
}

export interface CompressResult {
  file: File;
  base64: string; // "data:audio/mpeg;base64,..."
}

const fmt = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${(bytes / 1024).toFixed(1)} KB`
    : `${(bytes / (1024 * 1024)).toFixed(2)} MB`;

export async function compressAudio(input: File): Promise<CompressResult> {
  const ff = await getFFmpeg();
  const ext = input.name.split('.').pop() || 'bin';
  const inputName = `input.${ext}`;
  const outputName = 'output.mp3';

  console.log(`[FFmpeg] Compressing "${input.name}" (${fmt(input.size)}) → mono 16kHz 32kbps MP3`);
  const t0 = performance.now();

  ff.writeFile(inputName, await fetchFile(input));

  await ff.exec([
    '-i', inputName,
    '-ac', '1',       // mono
    '-ar', '16000',   // 16 kHz
    '-b:a', '32k',    // 32 kbps
    '-f', 'mp3',
    outputName,
  ]);

  const data = await ff.readFile(outputName) as Uint8Array;
  const blob = new Blob([data], { type: 'audio/mpeg' });
  const compressedFile = new File([blob], outputName, { type: 'audio/mpeg' });

  const elapsed = ((performance.now() - t0) / 1000).toFixed(2);
  const ratio = ((1 - blob.size / input.size) * 100).toFixed(1);
  console.log(`[FFmpeg] Done in ${elapsed}s — ${fmt(input.size)} → ${fmt(blob.size)} (${ratio}% smaller)`);

  const base64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  console.log(`[FFmpeg] Base64 encoded — ${fmt(base64.length)} total`);

  ff.deleteFile(inputName);
  ff.deleteFile(outputName);

  return { file: compressedFile, base64 };
}
